// MISSION MAP — Philippines → city → RouteZero site → mission briefing → existing lab.
//
// Presentation/navigation only. Everything shown is derived from the progression
// engine through src/map/mapState.js (read-only); starting a mission simply links to
// the existing unit route (#/unit/<d>/<u>), where the normal lab, CLI, validation and
// progression rules apply. The map never writes progress or storage.

import { h } from './h.js';
import { s, baseMap, backbone, WORLD_BOX } from '../map/mapRenderer.js';
import { cityDetail, districtAnchors } from '../map/cityMap.js';
import { siteScene } from '../map/siteMap.js';
import { createCamera } from '../map/mapInteractions.js';
import { CITIES, REGIONS, WATERS, COMPANY, SITE_BOX, project, siteLayout, siteToMap, missionKind, skillsFor, cityById, facilityAt } from '../map/mapData.js';
import { cityState, missionState, lockReason, operatorStats, mapHref, cityNetwork } from '../map/mapState.js';
import { resolveCityImage, resolveMissionImage } from '../map/imageData.js';

const CITY_BOX = { w: 19, h: 12 };
const SITE_MAX_W = 7.5; // below this view width the enterprise site is shown
const STATE_TEXT = { complete: 'Completed', current: 'Current', available: 'Available', locked: 'Locked', pending: 'Coming soon' };
const STATE_ICON = { complete: '✓', current: '●', available: '○', locked: '🔒', pending: '⋯' };
const DIFFICULTY = { LEARN: 'Intro', LAB: 'Core', TROUBLESHOOTING: 'Advanced', CHALLENGE: 'Capstone' };
const CALLOUT = { left: [-26, 0], right: [26, 0] };
const CALLOUT_OVERRIDE = { d01: [-30, 10], d05: [-26, -18], d06: [28, -2], d04: [28, 18] };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const pad2 = (n) => String(n).padStart(2, '0');
const cleanTitle = (t) => t.replace(/^FINAL CHALLENGE\s*[—–-]\s*/i, '');
const NET_TEXT = { online: 'Online', planned: 'Not yet built', outage: 'Outage' };

/** Illustration with a graceful fallback (missing file, rejected metadata). */
function illustration(img, { cls, testid, caption }) {
  const fallback = () => h('div', { class: `illo-fallback ${cls}`, 'data-testid': `${testid}-fallback`, role: 'img', 'aria-label': img.alt }, h('span', { 'aria-hidden': 'true' }, '▦'), h('span', {}, caption || 'Illustration unavailable'));
  if (img.fallback || !img.src) return h('figure', { class: `illo ${cls}`, 'data-testid': testid, 'data-fallback': 'true' }, fallback());
  const fig = h('figure', { class: `illo ${cls}`, 'data-testid': testid });
  const im = h('img', { src: img.src, alt: img.alt, loading: 'lazy', decoding: 'async', onerror: () => { fig.dataset.fallback = 'true'; im.replaceWith(fallback()); } });
  fig.append(im);
  if (img.credit) fig.append(h('figcaption', { class: 'illo-credit' }, img.creditURL ? h('a', { href: img.creditURL, target: '_blank', rel: 'noopener noreferrer' }, img.credit) : img.credit));
  return fig;
}
const ticketOf = (u) => (u.stages || []).map((st) => st.ticket).find((t) => t && typeof t === 'object');

export function mapView({ progress, curriculum, admin = false, support = null }) {
  const domainOf = (id) => curriculum.domains.find((d) => d.id === id);
  let route = { level: 'world' };

  // ------------------------------------------------------------------ stage
  const svgEl = s('svg', { class: 'map-svg', 'aria-hidden': 'true', preserveAspectRatio: 'none' });
  const base = baseMap();
  svgEl.append(base.defs, ...base.layers);
  const cityLayer = s('g', { class: 'map-cities' }, CITIES.map((c) => cityDetail(c)));
  svgEl.append(cityLayer, backbone((id) => progress.domainSummary(id).unlocked));
  const layouts = Object.fromEntries(CITIES.map((c) => [c.domainId, siteLayout(c, domainOf(c.domainId))]));
  const siteLayer = s('g', { class: 'map-sites' }, CITIES.map((c) => siteScene(c, layouts[c.domainId])));
  svgEl.append(siteLayer);

  const overlay = h('div', { class: 'map-overlay' });
  const leaders = s('svg', { class: 'map-leaders', 'aria-hidden': 'true' });
  const items = []; // { el, x, y, show(w), leader?, off? }
  const add = (el, x, y, show, extra = {}) => { overlay.append(el); const it = { el, x, y, show, ...extra }; items.push(it); return it; };

  // world: island-group and water labels
  for (const r of REGIONS) add(h('span', { class: 'map-region-label' }, r.name), ...project(...r.at), (w) => w > 260);
  for (const wtr of WATERS) add(h('span', { class: 'map-water-label' }, wtr.name), ...project(...wtr.at), (w) => w > 260);

  // world: city markers with callout labels
  for (const c of CITIES) {
    const d = domainOf(c.domainId);
    const st = cityState(progress, curriculum, c.domainId);
    const sum = progress.domainSummary(c.domainId);
    const [x, y] = project(...c.at);
    add(h('span', { class: `city-dot status-${st === 'pending' ? 'locked' : st} ${st}` }), x, y, (w) => w > 40);
    const off = CALLOUT_OVERRIDE[c.domainId] || CALLOUT[c.label];
    const btn = h('button', {
      class: `city-callout side-${off[0] < 0 ? 'left' : 'right'} ${st}`, 'data-testid': `map-city-${c.domainId}`, type: 'button',
      'aria-label': `${c.name} — Domain ${d.number} ${d.title} — ${STATE_TEXT[st]}`,
      onclick: () => { location.hash = mapHref({ level: 'world', domainId: c.domainId, preview: true }); },
    },
    h('span', { class: `cc-pin ${st}` }, STATE_ICON[st] === '●' ? d.number : st === 'complete' ? '✓' : st === 'locked' ? '🔒' : d.number),
    h('span', { class: 'cc-text' },
      h('span', { class: 'cc-name' }, c.name),
      h('span', { class: 'cc-sub' }, `D${d.number} · ${d.title}`),
      st === 'current' || st === 'available' ? h('span', { class: `cc-state ${st}` }, `${STATE_TEXT[st]} · ${sum.done}/${sum.total}`) : h('span', { class: 'sr-only' }, STATE_TEXT[st])));
    add(btn, x, y, (w) => w > 40, { off, leader: s('line', { class: `leader ${st}` }) });
  }

  // city: RouteZero facility markers + district labels
  for (const c of CITIES) {
    const st = cityState(progress, curriculum, c.domainId);
    add(h('span', { class: 'city-name-label' }, c.name), ...project(...c.at), (w) => w < 40 && w > SITE_MAX_W);
    for (const dl of districtAnchors(c)) add(h('span', { class: 'district-label' }, dl.name), dl.x, dl.y, (w) => w < 34 && w > SITE_MAX_W);
    const [x, y] = project(...c.site.at);
    add(h('button', {
      class: `site-marker ${st}`, type: 'button', 'data-testid': `map-site-${c.domainId}`, 'aria-label': `${c.site.name} — open the enterprise site`,
      onclick: () => { location.hash = mapHref({ level: 'site', domainId: c.domainId }); },
    }, h('span', { class: 'sm-icon', 'aria-hidden': 'true' }, '▦'), h('span', { class: 'sm-text' }, h('span', { class: 'sm-name' }, c.site.name), h('span', { class: 'sm-role' }, c.role))),
    x, y, (w) => w < 40 && w > SITE_MAX_W);
  }

  // city: network overlay (facilities + links) for cities that define one
  const netEls = []; // { domainId, kind:'link'|'fac', key, el }
  const netLayer = s('g', { class: 'map-citynet' });
  svgEl.insertBefore(netLayer, siteLayer);
  for (const c of CITIES.filter((x) => x.network)) {
    const pos = Object.fromEntries(c.network.facilities.map((f) => [f.id, facilityAt(c, f)]));
    for (const l of c.network.links) {
      const [x1, y1] = pos[l.a]; const [x2, y2] = pos[l.b];
      const line = s('line', { class: 'net-link', x1, y1, x2, y2, 'data-link': `${l.a}-${l.b}` }, s('title', {}, l.label));
      netLayer.append(line);
      netEls.push({ domainId: c.domainId, kind: 'link', key: `${l.a}-${l.b}`, el: line });
    }
    for (const f of c.network.facilities) {
      if (f.at === 'site') continue; // the HQ is the clickable site marker
      const el = h('div', { class: 'facility-marker', 'data-testid': `map-fac-${c.domainId}-${f.id}`, role: 'img' },
        h('span', { class: 'fm-dot', 'aria-hidden': 'true' }), h('span', { class: 'fm-text' }, h('b', {}, f.name), h('span', {}, f.role)));
      netEls.push({ domainId: c.domainId, kind: 'fac', key: f.id, el, f });
      add(el, ...pos[f.id], (w) => w < 40 && w > SITE_MAX_W);
    }
    const inc = h('a', { class: 'incident-marker', 'data-testid': `map-incident-${c.domainId}`, href: mapHref({ level: 'site', domainId: c.domainId, unitId: c.network.incidentUnit }) },
      h('span', { 'aria-hidden': 'true' }, '⚠ '), c.network.incidentLabel || 'ACTIVE INCIDENT');
    netEls.push({ domainId: c.domainId, kind: 'incident', el: inc });
    add(inc, ...pos[c.network.facilities.find((f) => f.at === 'site').id], (w) => w < 40 && w > SITE_MAX_W && inc.dataset.active === 'true', { off: [0, -62] });
  }
  function refreshNet() {
    for (const c of CITIES.filter((x) => x.network)) {
      const n = cityNetwork(progress, domainOf(c.domainId), c);
      const by = Object.fromEntries([...n.links.map((l) => [`link:${l.a}-${l.b}`, l]), ...n.facilities.map((f) => [`fac:${f.id}`, f])]);
      for (const e of netEls.filter((x) => x.domainId === c.domainId)) {
        if (e.kind === 'incident') { e.el.dataset.active = String(n.incident); continue; }
        const it = by[`${e.kind}:${e.key}`];
        e.el.setAttribute('data-state', it.state);
        e.el.setAttribute('class', `${e.kind === 'link' ? 'net-link' : 'facility-marker'} ${it.state}`);
        if (e.kind === 'fac') e.el.setAttribute('aria-label', `${it.name} — ${it.role} — ${NET_TEXT[it.state]}`);
      }
    }
  }
  refreshNet();

  // site: building labels + mission markers
  const markerEls = {};
  for (const c of CITIES) {
    const d = domainOf(c.domainId);
    const lay = layouts[c.domainId];
    for (const b of lay.buildings) add(h('span', { class: `bld-label slot-${b.slot}` }, b.name), ...siteToMap(c, b.x + b.w / 2, b.y + 1.6), (w) => w < SITE_MAX_W);
    for (const m of lay.markers) {
      const u = d.units[m.index];
      const ms = missionState(progress, d, u);
      const el = h('button', {
        class: `mission-marker ${ms.state} kind-${ms.kind.toLowerCase()} ${ms.incident ? 'incident' : ''}`, type: 'button',
        'data-testid': `map-mission-${c.domainId}-${u.id}`, 'data-state': ms.state,
        'aria-label': `Mission ${pad2(m.index + 1)} · ${u.title} — ${STATE_TEXT[ms.state]}${ms.incident ? ' · active incident' : ''}`,
        title: `Mission ${pad2(m.index + 1)} · ${u.title}`,
        onclick: () => { location.hash = mapHref({ level: 'site', domainId: c.domainId, unitId: u.id }); },
      }, ms.state === 'complete' ? '✓' : ms.state === 'locked' ? '🔒' : ms.kind === 'CHALLENGE' ? '★' : pad2(m.index + 1));
      markerEls[`${c.domainId}.${u.id}`] = el;
      add(el, ...siteToMap(c, m.x, m.y), (w) => w < SITE_MAX_W);
    }
  }
  leaders.append(...items.filter((i) => i.leader).map((i) => i.leader));

  // HUD
  const crumbs = h('nav', { class: 'map-crumbs', 'aria-label': 'Map location' });
  const hudTitle = h('div', { class: 'map-hud-title' });
  const ctl = (label, testid, fn, text) => h('button', { class: 'map-ctl', type: 'button', 'data-testid': testid, 'aria-label': label, title: label, onclick: fn }, text);
  const camRef = {};
  const controls = h('div', { class: 'map-controls' },
    ctl('World view', 'map-home', () => { location.hash = '#/map'; }, '⌂'),
    ctl('Back one level', 'map-back', () => back(), '←'),
    ctl('Zoom in', 'map-zoom-in', () => camRef.cam.zoomAt(1.6), '+'),
    ctl('Zoom out', 'map-zoom-out', () => camRef.cam.zoomAt(1 / 1.6), '−'),
    ctl('Reset map view', 'map-reset', () => frame(true), '⟲'));
  const briefing = h('aside', { class: 'map-panel briefing', 'data-testid': 'map-briefing', 'aria-live': 'polite' });
  briefing.hidden = true;
  const cityPanel = h('aside', { class: 'map-panel city-panel', 'data-testid': 'map-city-panel', 'aria-live': 'polite' });
  cityPanel.hidden = true;
  const stage = h('section', { class: 'map-stage', tabindex: '0', 'aria-label': 'Mission map — drag to pan, scroll or +/− to zoom', 'data-testid': 'map-stage' },
    svgEl, leaders, overlay,
    h('div', { class: 'map-hud' }, crumbs, hudTitle),
    controls, briefing, cityPanel,
    h('div', { class: 'map-attrib' }, `${COMPANY} · fictional learning world · stylised outline: Natural Earth (public domain)`));

  // ------------------------------------------------------------------ sidebar
  const sideLocation = h('section', { class: 'map-card panel', 'data-testid': 'map-location' });
  const side = h('aside', { class: 'map-side' },
    h('div', { class: 'map-intro' },
      h('div', { class: 'eyebrow' }, `${COMPANY.toUpperCase()} · PHILIPPINES`),
      h('h1', {}, 'Mission Map'),
      h('p', { class: 'muted' }, 'Explore the RouteZero Network across the Philippines. Choose a city, visit its site, and take on a mission.')),
    progressionCard(),
    currentMissionCard(),
    sideLocation,
    operatorCard(),
    legendCard(),
    admin ? adminCard() : null);

  const el = h('main', { class: 'page map-page', 'data-testid': 'map-page' }, side, stage);

  // ------------------------------------------------------------------ camera
  const cam = createCamera(stage, svgEl, { onFrame: render });
  camRef.cam = cam;
  function render(c) {
    const v = c.view; const { W, H } = c.size();
    leaders.setAttribute('viewBox', `0 0 ${W} ${H}`);
    cityLayer.style.opacity = clamp((70 - v.w) / 45, 0, 1);
    netLayer.style.opacity = clamp((40 - v.w) / 14, 0, 1) * clamp((v.w - SITE_MAX_W + 1) / 2.5, 0, 1);
    siteLayer.style.opacity = clamp((12 - v.w) / 4.5, 0, 1);
    for (const it of items) {
      const [px, py] = c.toScreen(it.x, it.y);
      const vis = it.show(v.w) && px > -160 && px < W + 160 && py > -60 && py < H + 60;
      if (it.el.hidden === vis) it.el.hidden = !vis;
      if (!vis) { if (it.leader) it.leader.setAttribute('visibility', 'hidden'); continue; }
      const ox = it.off ? it.off[0] : 0; const oy = it.off ? it.off[1] : 0;
      it.el.style.transform = `translate(${(px + ox).toFixed(1)}px, ${(py + oy).toFixed(1)}px)`;
      if (it.leader) { Object.entries({ x1: px, y1: py, x2: px + ox * 0.8, y2: py + oy * 0.8 }).forEach(([k, val]) => it.leader.setAttribute(k, val.toFixed(1))); it.leader.setAttribute('visibility', 'visible'); }
    }
  }
  function targetBox(r) {
    if (r.level === 'world') return WORLD_BOX;
    const c = cityById(r.domainId);
    const [sx, sy] = project(...c.site.at);
    if (r.level === 'site' && r.unitId) {
      // keep the whole site visible beside (desktop) or above (phone) the briefing card
      const { W, H } = cam.size(); const A = W / H;
      if (W < 960) {
        const w = Math.max(SITE_BOX.w * 1.1, SITE_BOX.h * 1.1 * A); const h = w / A;
        return { cx: sx, cy: sy + h * 0.17, w, h };
      }
      const f = Math.max(0.4, (W - 470) / W);
      const w = Math.max((SITE_BOX.w * 1.12) / f, SITE_BOX.h * 1.25 * A); const h = w / A;
      return { cx: sx + w * (0.5 - f / 2), cy: sy, w, h };
    }
    if (r.level === 'site') return { cx: sx, cy: sy, w: SITE_BOX.w * 1.25, h: SITE_BOX.h * 1.35 };
    const [cx, cy] = project(...c.at);
    return { cx: (cx + sx) / 2, cy: (cy + sy) / 2, ...CITY_BOX };
  }
  function frame(animate) { const b = targetBox(route); return animate ? cam.flyTo(b) : cam.set(cam.fit(b)); }
  function back() {
    if (route.level === 'site' && route.unitId) location.hash = mapHref({ level: 'site', domainId: route.domainId });
    else if (route.level === 'site') location.hash = mapHref({ level: 'city', domainId: route.domainId });
    else location.hash = '#/map';
  }

  // ------------------------------------------------------------------ navigation
  let mounted = false;
  function navigate(next) {
    const prev = route;
    route = next;
    el.dataset.level = route.level;
    stage.dataset.level = route.level;
    refreshNet(); drawCrumbs(); drawLocation(); drawBriefing(); drawCityPanel();
    for (const [k, m] of Object.entries(markerEls)) m.classList.toggle('is-selected', route.unitId && k === `${route.domainId}.${route.unitId}`);
    if (!mounted) return;
    const sameSite = prev.level === 'site' && route.level === 'site' && prev.domainId === route.domainId;
    frame(true).then(() => {
      if (sameSite || route.unitId) briefing.querySelector('h2')?.focus?.({ preventScroll: true });
      else if (route.preview) cityPanel.querySelector('h2')?.focus?.({ preventScroll: true });
    });
  }
  function drawCrumbs() {
    crumbs.textContent = '';
    const parts = [{ label: 'Philippines', href: '#/map', id: 'world' }];
    const c = route.domainId && route.level !== 'world' ? cityById(route.domainId) : null;
    if (c) parts.push({ label: c.name, href: mapHref({ level: 'city', domainId: c.domainId }), id: 'city' });
    if (c && route.level === 'site') parts.push({ label: c.site.name, href: mapHref({ level: 'site', domainId: c.domainId }), id: 'site' });
    parts.forEach((p, i) => crumbs.append(i ? h('span', { class: 'sep', 'aria-hidden': 'true' }, '›') : '',
      i === parts.length - 1 ? h('span', { class: 'crumb current', 'aria-current': 'location' }, p.label) : h('a', { class: 'crumb', href: p.href, 'data-testid': `map-crumb-${p.id}` }, p.label)));
    hudTitle.textContent = route.level === 'world' ? `${CITIES.length} cities · RouteZero backbone` : route.level === 'city' ? `${c.region} · ${c.role}` : `${c.name} · ${domainOf(c.domainId).title}`;
  }

  function drawLocation() {
    sideLocation.textContent = '';
    if (route.level === 'world') {
      sideLocation.append(h('div', { class: 'rc-title' }, 'ROUTEZERO CITIES'),
        h('ul', { class: 'map-list' }, CITIES.map((c) => {
          const d = domainOf(c.domainId); const st = cityState(progress, curriculum, c.domainId); const sum = progress.domainSummary(c.domainId);
          return h('li', {}, h('a', { class: `map-row ${st}`, href: mapHref({ level: 'city', domainId: c.domainId }), 'data-testid': `map-row-${c.domainId}` },
            h('span', { class: `mr-pin ${st}`, 'aria-hidden': 'true' }, st === 'complete' ? '✓' : st === 'locked' ? '🔒' : d.number),
            h('span', { class: 'mr-text' }, h('b', {}, c.name), h('span', {}, `D${d.number} · ${d.title}`)),
            h('span', { class: `chip ${chipClass(st)}` }, st === 'current' || st === 'available' ? `${sum.done}/${sum.total}` : STATE_TEXT[st].toUpperCase())));
        })));
      return;
    }
    const c = cityById(route.domainId); const d = domainOf(c.domainId);
    const st = cityState(progress, curriculum, d.id); const sum = progress.domainSummary(d.id);
    sideLocation.append(
      h('div', { class: 'rc-title' }, route.level === 'city' ? `${c.region.toUpperCase()} · CITY` : 'ROUTEZERO SITE', h('span', { class: `chip ${chipClass(st)}` }, STATE_TEXT[st].toUpperCase())),
      h('h2', {}, route.level === 'city' ? c.name : c.site.name),
      h('p', { class: 'muted small' }, `${route.level === 'city' ? c.role : c.name} · Domain ${d.number} — ${d.title}`),
      h('p', { class: 'small' }, c.description),
      h('div', { class: 'bar', role: 'progressbar', 'aria-valuenow': sum.pct, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-label': 'Missions complete' }, h('span', { style: { width: `${sum.pct}%` } })),
      h('div', { class: 'muted small' }, `${sum.done}/${sum.total} missions complete${sum.pending ? ` · ${sum.pending} coming soon` : ''}`));
    if (route.level === 'city') {
      sideLocation.append(h('div', { class: 'row' },
        h('a', { class: 'btn primary small', href: mapHref({ level: 'site', domainId: d.id }), 'data-testid': 'map-enter-site' }, `Enter ${c.site.name} →`),
        sum.unlocked ? h('a', { class: 'btn ghost small', href: `#/domain/${d.id}` }, 'Domain overview') : null));
      return;
    }
    sideLocation.append(h('ol', { class: 'map-missions' }, d.units.map((u, i) => {
      const ms = missionState(progress, d, u);
      return h('li', {}, h('a', {
        class: `map-mission-row ${ms.state} ${route.unitId === u.id ? 'is-selected' : ''}`, href: mapHref({ level: 'site', domainId: d.id, unitId: u.id }), 'data-testid': `map-mrow-${u.id}`,
      }, h('span', { class: `mm-dot ${ms.state} ${ms.incident ? 'incident' : ''}`, 'aria-hidden': 'true' }, ms.state === 'complete' ? '✓' : ms.state === 'locked' ? '🔒' : pad2(i + 1)),
      h('span', { class: 'mmr-title' }, cleanTitle(u.title)),
      h('span', { class: `u-kind ${ms.kind.toLowerCase()}` }, ms.kind)));
    })));
  }

  function drawCityPanel() {
    cityPanel.textContent = '';
    if (route.level !== 'world' || !route.preview) { cityPanel.hidden = true; return; }
    const c = cityById(route.domainId); const d = domainOf(c.domainId);
    const st = cityState(progress, curriculum, d.id); const sum = progress.domainSummary(d.id);
    cityPanel.hidden = false;
    cityPanel.className = `map-panel city-panel ${st}`;
    cityPanel.append(...[
      h('div', { class: 'bf-head' },
        h('span', { class: 'bf-eyebrow' }, `${c.region.toUpperCase()} · DOMAIN ${d.number}`),
        h('button', { class: 'btn ghost small bf-close', type: 'button', 'data-testid': 'city-panel-close', 'aria-label': 'Close city information', onclick: () => { location.hash = '#/map'; } }, '✕')),
      illustration(resolveCityImage(d.id), { cls: 'city-illo', testid: 'city-panel-image', caption: c.name }),
      h('h2', { tabindex: '-1' }, c.name),
      h('div', { class: 'bf-chips' }, h('span', { class: `chip ${chipClass(st)}`, 'data-testid': 'city-panel-state' }, `${STATE_ICON[st]} ${STATE_TEXT[st].toUpperCase()}`)),
      h('dl', { class: 'bf-facts' },
        h('dt', {}, 'Region'), h('dd', {}, c.region),
        h('dt', {}, 'RouteZero site'), h('dd', {}, c.site.name),
        h('dt', {}, 'Domain'), h('dd', {}, `${d.number} — ${d.title}`),
        h('dt', {}, 'Network role'), h('dd', {}, c.role)),
      h('p', { class: 'small' }, c.description),
      h('div', { class: 'bar', role: 'progressbar', 'aria-valuenow': sum.pct, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-label': 'Mission progress' }, h('span', { style: { width: `${sum.pct}%` } })),
      h('div', { class: 'muted small', 'data-testid': 'city-panel-progress' }, `Mission progress: ${sum.done}/${sum.total} complete${sum.pending ? ` · ${sum.pending} coming soon` : ''}`),
      h('div', { class: 'row' }, h('a', { class: 'btn primary', href: mapHref({ level: 'city', domainId: d.id }), 'data-testid': 'city-panel-enter' }, 'ENTER CITY →')),
    ]);
  }

  function drawBriefing() {
    briefing.textContent = '';
    if (route.level !== 'site' || !route.unitId) { briefing.hidden = true; return; }
    const c = cityById(route.domainId); const d = domainOf(c.domainId);
    const u = d.units.find((x) => x.id === route.unitId);
    if (!u) { briefing.hidden = true; return; }
    const i = d.units.indexOf(u);
    const ms = missionState(progress, d, u);
    const lay = layouts[d.id].markers.find((m) => m.unitId === u.id);
    const bld = layouts[d.id].buildings.find((b) => b.slot === lay.slot);
    const tk = u.implemented ? ticketOf(u) : null;
    const faults = u.faultCatalog ? new Set(Object.values(u.faultCatalog).map((f) => f.group)).size : 0;
    const reason = lockReason(progress, curriculum, d, u);
    const cta = { complete: 'Review mission', current: progress.unitState(d.id, u.id) === 'in-progress' ? 'Continue mission' : 'Start mission', available: progress.unitState(d.id, u.id) === 'in-progress' ? 'Continue mission' : 'Start mission' }[ms.state];
    briefing.hidden = false;
    briefing.className = `map-panel briefing ${ms.state} ${ms.incident ? 'incident' : ''}`;
    const parts = [
      h('div', { class: 'bf-head' },
        h('span', { class: 'bf-eyebrow' }, ms.incident && tk ? `INCIDENT #${tk.id}` : tk ? `TICKET #${tk.id}` : `MISSION ${pad2(i + 1)}`),
        h('button', { class: 'btn ghost small bf-close', type: 'button', 'data-testid': 'briefing-close', 'aria-label': 'Close briefing', onclick: () => { location.hash = mapHref({ level: 'site', domainId: d.id }); } }, '✕')),
      illustration(resolveMissionImage(d.id, u, ms.kind), { cls: 'mission-illo', testid: 'briefing-image', caption: ms.kind }),
      h('h2', { tabindex: '-1' }, `Mission ${pad2(i + 1)} · ${cleanTitle(u.title)}`),
      h('div', { class: 'bf-chips' },
        h('span', { class: `chip kind-${ms.kind.toLowerCase()}` }, ms.kind === 'CHALLENGE' ? 'FINAL CHALLENGE' : ms.kind),
        h('span', { class: 'chip' }, DIFFICULTY[ms.kind]),
        h('span', { class: `chip ${chipClass(ms.state)}`, 'data-testid': 'briefing-state' }, `${STATE_ICON[ms.state]} ${STATE_TEXT[ms.state].toUpperCase()}`),
        ms.incident ? h('span', { class: 'chip status-danger bf-incident' }, '⚠ ACTIVE INCIDENT') : null),
      h('dl', { class: 'bf-facts' },
        h('dt', {}, 'Site'), h('dd', {}, `${c.site.name} — ${bld.name}`),
        h('dt', {}, 'City'), h('dd', {}, `${c.name} · ${c.region}`),
        tk ? [h('dt', {}, 'Ticket'), h('dd', {}, tk.subject)] : null,
        faults ? [h('dt', {}, 'Faults'), h('dd', { class: 'bf-faults' }, `${faults} hidden faults · CLI evidence only`)] : null),
      u.objective ? h('p', { class: 'bf-objective' }, h('b', {}, 'Objective  '), u.objective) : h('p', { class: 'bf-objective muted' }, 'This mission is on the roadmap and not yet built.'),
      h('div', { class: 'bf-skills' }, skillsFor(u, d).map((t) => h('span', { class: 'bf-skill' }, t))),
      u.implemented ? h('div', { class: 'bf-reward muted small' }, `Reward on verified completion: +${ms.kind === 'CHALLENGE' ? 500 : 100} XP`) : null,
      reason
        ? h('div', { class: 'bf-locked', 'data-testid': 'briefing-locked' }, h('span', { 'aria-hidden': 'true' }, ms.state === 'pending' ? '⋯ ' : '🔒 '), reason)
        : h('div', { class: 'row' },
          h('a', { class: 'btn primary', href: `#/unit/${d.id}/${u.id}`, 'data-testid': 'briefing-start' }, `${cta} →`),
          h('a', { class: 'btn ghost small', href: `#/domain/${d.id}` }, 'Domain overview')),
    ];
    briefing.append(...parts.filter(Boolean)); // native append() would print "null"
  }

  // ------------------------------------------------------------------ sidebar cards
  // The whole RouteZero journey: Level 1 Support Desk first, then the seven domains.
  function progressionCard() {
    const sup = support?.summary?.() || null;
    const supState = !sup ? null : sup.complete ? 'complete' : 'current';
    const firstOpen = curriculum.domains.find((d) => { const x = progress.domainSummary(d.id); return x.unlocked && !x.complete; });
    // A new learner starts at the Support Desk; the first domain is shown as 'Next' until onboarding is done.
    const onboarding = !!sup && !sup.complete && !Object.values(progress.raw?.().units || {}).some((r) => r.status === 'complete');
    const rows = [];
    if (sup) {
      rows.push(h('li', { class: `mp-row ${supState}`, 'data-state': supState, 'data-testid': 'progression-support' },
        h('a', { href: '#/support' },
          h('span', { class: 'mp-num', 'aria-hidden': 'true' }, supState === 'complete' ? '✓' : 'L1'),
          h('span', { class: 'mp-name' }, h('b', {}, 'Level 1 Support Desk'), h('small', {}, `${sup.done}/${sup.total} tickets · onboarding`)),
          h('span', { class: 'mp-state' }, supState === 'complete' ? 'Complete' : 'Start here'))));
    }
    for (const d of curriculum.domains) {
      const x = progress.domainSummary(d.id);
      const st = x.complete ? 'complete' : x.pending === x.total ? 'pending' : x.unlocked ? (d === firstOpen && !onboarding ? 'current' : 'available') : 'locked';
      const c = cityById(d.id);
      rows.push(h('li', { class: `mp-row ${st}`, 'data-state': st, 'data-testid': `progression-${d.id}` },
        h('a', { href: mapHref({ level: 'world', domainId: d.id, preview: true }) },
          h('span', { class: 'mp-num', 'aria-hidden': 'true' }, st === 'complete' ? '✓' : d.number),
          h('span', { class: 'mp-name' }, h('b', {}, d.title), h('small', {}, `${c ? c.name : ''}${x.implemented ? ` · ${x.done}/${x.implemented} missions` : ''}`)),
          h('span', { class: 'mp-state' }, { complete: 'Complete', current: 'Current', available: onboarding && d === firstOpen ? 'Next' : 'Open', pending: 'Coming soon', locked: 'Locked' }[st]))));
    }
    return h('section', { class: 'map-card panel mp-card', 'data-testid': 'mission-progression' },
      h('div', { class: 'eyebrow' }, 'MISSION PROGRESSION'),
      h('ol', { class: 'mp-list' }, rows),
      h('p', { class: 'muted small' }, 'Next: Security Labs (Firewalls · Network Security) — coming soon. See Profile & Career.'));
  }
  function currentMissionCard() {
    const sup = !admin ? support?.summary?.() : null;
    const anyDone = Object.values(progress.raw?.().units || {}).some((r) => r.status === 'complete');
    if (sup && !sup.complete && !anyDone) {
      return h('section', { class: 'map-card panel-dark', 'data-testid': 'map-current' }, h('div', { class: 'mission-eyebrow' }, 'CURRENT MISSION'),
        h('div', { class: 'mc-num' }, `LEVEL 1 SUPPORT DESK · ${sup.done}/${sup.total} TICKETS`),
        h('h2', { class: 'mc-title' }, 'Start with your first support tickets'),
        h('p', { class: 'small' }, 'RouteZero Technologies Support Desk · onboarding'),
        h('div', { class: 'row' }, h('a', { class: 'btn primary small', href: '#/support', 'data-testid': 'map-continue' }, '▶ OPEN SUPPORT DESK')));
    }
    const pick = curriculum.domains.find((d) => { const x = progress.domainSummary(d.id); return x.unlocked && !x.complete; });
    const next = pick ? progress.currentUnit(pick.id) : null;
    const c = pick ? cityById(pick.id) : null;
    const card = h('section', { class: 'map-card panel-dark', 'data-testid': 'map-current' }, h('div', { class: 'mission-eyebrow' }, 'CURRENT MISSION'));
    if (!next) return card.append(h('p', {}, pick ? `${c.name}: IMPLEMENTATION PENDING — new missions are being built.` : 'All available missions complete.')), card;
    const i = pick.units.indexOf(next);
    card.append(
      h('div', { class: 'mc-num' }, `MISSION ${pad2(i + 1)} · D${pick.number}`),
      h('h2', { class: 'mc-title' }, cleanTitle(next.title)),
      h('p', { class: 'small' }, `${c.name} · ${c.site.name}`),
      h('div', { class: 'row' },
        h('a', { class: 'btn primary small', href: `#/unit/${pick.id}/${next.id}`, 'data-testid': 'map-continue' }, '▶ CONTINUE'),
        h('a', { class: 'btn small', href: mapHref({ level: 'site', domainId: pick.id, unitId: next.id }), 'data-testid': 'map-locate' }, '◎ Show on map')));
    return card;
  }
  function operatorCard() {
    const o = operatorStats(progress, curriculum);
    return h('section', { class: 'map-card panel-dark', 'data-testid': 'map-operator' },
      h('div', { class: 'mission-eyebrow' }, 'LEVEL · XP · MISSION PROGRESS'),
      h('div', { class: 'op-level' }, h('b', {}, `LVL ${o.level}`), h('span', {}, `${o.xp.toLocaleString()} XP`)),
      h('div', { class: 'bar' }, h('span', { style: { width: `${((500 - o.toNext) / 500) * 100}%` } })),
      h('p', { class: 'small' }, `${o.done} of ${o.open} live missions closed · ${o.toNext} XP to next level`),
      h('div', { class: 'muted small' }, 'XP is a display of verified completions only.'));
  }
  function legendCard() {
    const rows = [['complete', '✓', 'Completed'], ['current', '●', 'Current'], ['available', '○', 'Available'], ['incident', '⚠', 'Active incident'], ['locked', '🔒', 'Locked'], ['pending', '⋯', 'Coming soon']];
    return h('section', { class: 'map-card panel-dark' }, h('div', { class: 'mission-eyebrow' }, 'MAP LEGEND'),
      h('ul', { class: 'map-legend' }, rows.map(([k, ic, t]) => h('li', {}, h('span', { class: `lg-dot ${k}`, 'aria-hidden': 'true' }, ic), t))),
      h('p', { class: 'small' }, 'Drag to pan · scroll or +/− to zoom · double-click to zoom in.'));
  }
  function adminCard() {
    const sel = h('select', { 'data-testid': 'map-teleport', 'aria-label': 'Teleport to a map location', onchange: (e) => { if (e.target.value) location.hash = e.target.value; } },
      h('option', { value: '' }, 'Teleport to…'), h('option', { value: '#/map' }, 'Philippines (world view)'),
      CITIES.map((c) => [h('option', { value: mapHref({ level: 'city', domainId: c.domainId }) }, `${c.name} — city`), h('option', { value: mapHref({ level: 'site', domainId: c.domainId }) }, `${c.site.name} — site`)]));
    return h('section', { class: 'map-card panel admin-tools', 'data-testid': 'map-admin' },
      h('div', { class: 'rc-title' }, 'ADMIN · MAP TEST CENTER'),
      h('p', { class: 'small' }, 'The map shows the admin sandbox. Every city and mission can be opened; learner progress is not touched.'), sel);
  }

  // ------------------------------------------------------------------ lifecycle
  return {
    el, kind: 'map',
    navigate,
    mount() {
      mounted = true;
      cam.set(cam.fit(WORLD_BOX));
      if (route.level === 'world') return;
      requestAnimationFrame(() => frame(true));
    },
    destroy() { cam.destroy(); },
  };
}

function chipClass(st) { return { complete: 'complete', current: 'available', available: 'unlocked', locked: 'locked', pending: 'pending' }[st] || ''; }
